import { useState, useEffect } from 'react';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { viewReferredOutReasons, createReferredOutReason, updateReferredOutReason } from 'services/leadServices';
import { handlePageNumberInPagination } from 'common/pagination';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { totalCountEvent } from 'services/generalServices';

const useReferredOutReasons = (loadTotalCount = false, practice_area_id = '') => {
  const [referredOutReasonList, setReferredOutReasonList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reasonDetails, setReasonDetails] = useState(null);
  const [pageLimit, setPageLimit] = useState(10);
  const [isForward, setIsForward] = useState(true);
  const [sortOrder, setSortOrder] = useState('alphabet_ascending');

  const { addToast } = useToast();
  const userContext = useUserDetailsContext();

  // Get pagination tokens
  const nextToken = reasonDetails?.data?.next_token;
  const transitionToken = reasonDetails?.data?.transition_token;

  const loadReferredOutReasons = async (
    limit = pageLimit,
    token = '',
    direction = 'forward',
    sort_order = sortOrder,
    practice_area_id = ''
  ) => {
    setLoading(true);

    try {
      const response = await viewReferredOutReasons(limit, token, direction, sort_order, practice_area_id);

      if (response?.data) {
        setReferredOutReasonList(response?.data['referred_out_reasons']);
        setReasonDetails(response);
      } else {
        setReferredOutReasonList([]);
        setReasonDetails(null);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const createReason = async (reasonData) => {
    setLoading(true);

    try {
      const response = await createReferredOutReason(reasonData);
      addToast(
        false,
        toastConstant.toasterType.SUCCESS,
        toastConstant.api.SUCCESS,
        toastConstant.message.CREATED_REFERRED_OUT_REASON_SUCCESS
      );

      await loadReferredOutReasons(pageLimit, '', 'forward', sortOrder);
      return response;
    } catch (err) {
      console.log(err);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.CREATE_REFERRED_OUT_REASON_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const updateReason = async (referredOutReasonSk, reasonData) => {
    setLoading(true);

    try {
      const response = await updateReferredOutReason(referredOutReasonSk, reasonData);
      addToast(
        false,
        toastConstant.toasterType.SUCCESS,
        toastConstant.api.SUCCESS,
        toastConstant.message.UPDATED_REFERRED_OUT_REASON_SUCCESS
      );

      await loadReferredOutReasons(pageLimit, '', 'forward', sortOrder);
    } catch (err) {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.UPDATE_REFERRED_OUT_REASON_FAILED);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => {
    loadReferredOutReasons(pageLimit, '', 'forward', sortOrder);
  };

  // Pagination functions
  const onNextPageClick = () => {
    loadReferredOutReasons(pageLimit, !isForward ? transitionToken : nextToken, 'forward', sortOrder);
    setIsForward(true);
  };

  const onPrevPageClick = () => {
    loadReferredOutReasons(pageLimit, isForward ? transitionToken : nextToken, 'reverse', sortOrder);
    setIsForward(false);
  };

  const onPageChange = (event) => {
    setPageLimit(event.value);
    handlePageNumberInPagination(userContext, 'settings', 'custom_descriptors', 'referred_out_reasons', event.value);
    loadReferredOutReasons(event.value, '', 'forward', sortOrder);
    setIsForward(true);
  };

  // Handler for sort change
  const onSortChange = (newSortOrder) => {
    setSortOrder(newSortOrder);
    loadReferredOutReasons(pageLimit, '', 'forward', newSortOrder);
    setIsForward(true);
  };

  useEffect(() => {
    const initializeData = async () => {
      let initialPageLimit = 10;

      if (loadTotalCount) {
        try {
          const totalCountResponse = await totalCountEvent('referred_out_reason');
          initialPageLimit = totalCountResponse?.data || 10;
        } catch (err) {
          console.log('Error loading total count:', err);
          initialPageLimit = 10;
        }
      } else {
        if (userContext?.firmDetails?.pagination_preference?.settings?.custom_descriptors?.referred_out_reasons) {
          initialPageLimit = userContext?.firmDetails?.pagination_preference?.settings?.custom_descriptors?.referred_out_reasons;
        }
      }
      setPageLimit(initialPageLimit);
      loadReferredOutReasons(initialPageLimit, '', 'forward', 'alphabet_ascending');
    };

    initializeData();
  }, [practice_area_id]);

  return {
    referredOutReasonList,
    loading,
    createReason,
    updateReason,
    refresh,
    setReferredOutReasonList,
    // Pagination
    nextToken,
    transitionToken,
    isForward,
    pageLimit,
    onNextPageClick,
    onPrevPageClick,
    onPageChange,
    // Sorting
    sortOrder,
    onSortChange,
  };
};

export default useReferredOutReasons;
